/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module ShareserverTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

/**
 * Array of GFE SDK Permission Scopes and their corresponding settings
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SDKPermissionsArray".
 */
export type SdkPermissionsArray = {
    scope?: SdkScopeType;
    permission?: SdkPermissions;
}[];

export interface ShareServer {
    [k: string]: any;
}
/**
 * Data for a generated Higlight
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Highlight".
 */
export interface Highlight {
    /**
     * Internal identifier value of the Highlight
     */
    id?: number;
    /**
     * Application-sourced definition identifier of the Highlight
     */
    definitionId?: string;
    /**
     * Application-sourced name of the Highlight
     */
    name?: string;
    /**
     * Application-sourced group identifier that the Highlight belongs to
     */
    groupId?: string;
    type?: Type;
    /**
     * Fully-quantified path of the Highlight
     */
    path?: string;
    /**
     * Application-sourced tag values for the Highlight
     */
    tags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    significance?: number;
    /**
     * Length of the video, in seconds (defined only if Highlight type is video, videos less than 1 second report as 0)
     */
    videoLengthSeconds?: number;
}
/**
 * DRS-sourced information about an application
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "HighlightAppInfo".
 */
export interface HighlightAppInfo {
    /**
     * DRS-sourced short name of the application for the Highlight
     */
    appShortName?: string;
    /**
     * DRS-sourced name of the application for the Highlight
     */
    name?: string;
    /**
     * DRS-sourced profile name of the application for the Highlight
     */
    profileName?: string;
}
/**
 * Application-defined data about a Highlight type
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "HighlightDefinition".
 */
export interface HighlightDefinition {
    /**
     * Application-sourced definition identifier of the Highlight
     */
    definitionId?: string;
    /**
     * Application-sourced name of the Highlight
     */
    name?: string;
    /**
     * Defines if the user has enabled this type of highlight
     */
    userEnabled?: boolean;
    /**
     * Application-sourced tag values for the Highlight
     */
    tags?: number;
    /**
     * Application-sourced significance values for the Highlight
     */
    significance?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetCaptureSupportedParameters".
 */
export interface SetCaptureSupportedParameters {
    /**
     * Flag indicating all capturing is disabled
     */
    supported: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterHotkeyNotificationsReturn".
 */
export interface RegisterHotkeyNotificationsReturn {
    hotkey: HotkeyEnum;
    /**
     * Hotkey state
     */
    state?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterGeneralNotificationsReturn".
 */
export interface RegisterGeneralNotificationsReturn {
    /**
     * Screenshot saved notification
     */
    screenshotSaved?: {
        /**
         * result
         */
        result?: number;
    };
    /**
     * ShadowPlay restarted notification
     */
    shadowPlayRestarted?: string;
    /**
     * Notification when ShadowPlay capture failed
     */
    captureFailed?: {
        /**
         * result
         */
        result?: number;
        captureMode?: CaptureModeEnum;
    };
    /**
     * Notification when cloud session state changes
     */
    cloudSessionState?: {
        state?: CloudSessionStateType;
    };
    /**
     * Notification when freeStyle Ansel State is toggled
     */
    freeStyleAnselState?: {
        /**
         * flag for the state
         */
        enable?: boolean;
    };
    /**
     * Notification when ShadowPlay disable HDR Warning
     */
    disableHDR?: {
        captureMode?: CaptureModeEnum;
    };
    /**
     * Notification for the state of the space in the gallery
     */
    galleryIsFull?: boolean;
    /**
     * Notification for the state of the space on the gallery disk
     */
    diskSpaceIsLow?: boolean;
    /**
     * Recording saved notification
     */
    hotkeyChanged?: {
        hotkey?: HotkeyEnum;
    };
    /**
     * Game exit notification
     */
    gameExited?: {
        /**
         * Process id of the game
         */
        exitAppPID?: number;
    };
    /**
     * MIC/WebCam plug in/out notification
     */
    deviceChange?: {
        device?: DeviceTypeEnum;
        state?: DeviceStateTypeEnum;
        name?: string;
        id?: string;
    };
    /**
     * Protected Content running notification
     */
    protectedContentRunning?: {
        appname?: string;
    };
    /**
     * Notification when GAssist is installed/uninstalled
     */
    gassistInstallState?: {
        /**
         * flag for the state
         */
        installed?: boolean;
    };
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetTelemtryInfoToShadowPlayParameters".
 */
export interface SetTelemtryInfoToShadowPlayParameters {
    /**
     * userId of logged in user
     */
    userId?: string;
    /**
     * externalUserId of logged in user
     */
    externalUserId?: string;
    /**
     * idpId of logged in user account
     */
    idpId?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterCaptureStateChangeNotificationsReturn".
 */
export interface RegisterCaptureStateChangeNotificationsReturn {
    captureMode?: CaptureModeEnum;
    recordingState?: RecordingState;
    /**
     * Indicates whether media is captured using Recode or transcode
     */
    captureMethod?: string;
    /**
     * Indicates width of the captured media
     */
    recordWidth?: number;
    /**
     * Indicates height of the captured media
     */
    recordHeight?: number;
    /**
     * Indicates framerate of the captured media
     */
    recordFramerate?: number;
    /**
     * Indicates target codec for the captured media
     */
    recordCodec?: string;
    /**
     * Indicates media color space viz SDR, HDR.
     */
    colorSpaceRange?: string;
    /**
     * Indicating type of error
     */
    errorType?: string;
    /**
     * Additional information specific to the CaptureEvent error
     */
    errorDetails?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterWindowStateChangeNotificationsReturn".
 */
export interface RegisterWindowStateChangeNotificationsReturn {
    /**
     * Type of notification
     */
    windowMsg?: string;
    /**
     * Indicates entering/leaving fullscreen
     */
    direction?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterBroadcastSessionNotificationsReturn".
 */
export interface RegisterBroadcastSessionNotificationsReturn {
    /**
     * Broadcast session event
     */
    sessionEvent?: string;
    /**
     * Game name
     */
    titleSource?: string;
    /**
     * Telemetry name
     */
    spTelemetryName?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CaptureSavedNotificationParameters".
 */
export interface CaptureSavedNotificationParameters {
    captureMode?: CaptureModeEnum;
    /**
     * Fully-quantified path to the recording
     */
    path?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "HighlightSavedNotificationParameters".
 */
export interface HighlightSavedNotificationParameters {
    app?: HighlightAppInfo;
    highlight?: Highlight;
    /**
     * flag indicating to hide notification on HL capture
     */
    unannounced?: boolean;
    /**
     * SDK version
     */
    sdkVersion?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "HighlightCaptureFailureNotificationParameters".
 */
export interface HighlightCaptureFailureNotificationParameters {
    highlight?: Highlight;
    /**
     * Error code indicating highlight capture failure
     */
    errorCode?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterSDKNotificationsReturn".
 */
export interface RegisterSDKNotificationsReturn {
    /**
     * SDK Create Instance notification
     */
    createInstance?: {
        app?: HighlightAppInfo;
        /**
         * SDK version
         */
        sdkVersion?: string;
    };
    /**
     * SDK Destroy Instance notification
     */
    destroyInstance?: {
        app?: HighlightAppInfo;
    };
    /**
     * Indicates highlights group was opened by SDK application
     */
    openGroup?: boolean;
    /**
     * Indicates highlights group was closed by SDK application
     */
    closeGroup?: boolean;
    /**
     * Indicates status of SDK API call
     */
    actionStatus?: {
        /**
         * API name
         */
        action?: string;
        /**
         * SDK API call status
         */
        status?: string;
        /**
         * Internal code of SDK API call status
         */
        statusInternalCode?: number;
        app?: HighlightAppInfo;
    };
    /**
     * Permissions request
     */
    requestPermissions?: {
        app?: HighlightAppInfo;
        /**
         * Scopes
         */
        scopes?: SdkScopeType[];
    };
    /**
     * Permissions request
     */
    updatePermissions?: {
        app: HighlightAppInfo;
        permissions: SdkPermissionsArray;
    };
    /**
     * Indicates manual record is running with another game
     */
    manualRecordRunning?: {
        /**
         * Manual record game
         */
        manualRecordGameName?: string;
        /**
         * SDK Game
         */
        gameName?: string;
    };
    /**
     * Indicates broadcast is running with another game
     */
    broadcastRunning?: {
        /**
         * Broadcast record game
         */
        broadcastGameName?: string;
        /**
         * SDK Game
         */
        gameName?: string;
    };
    /**
     * Indicates Highlights Session is enabled
     */
    highlightSessionStatus?: {
        /**
         * Enable Flag
         */
        enable?: boolean;
    };
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "RegisterAppNotificationsReturn".
 */
export interface RegisterAppNotificationsReturn {
    /**
     * Triggered when a SP-enabled game or application starts up
     */
    appStarted?: {
        /**
         * Process ID of the app
         */
        pid?: number;
        /**
         * CMS ID of the app
         */
        cmsId?: number;
        /**
         * Chroma App ID of the app
         */
        ChromaAppId?: string;
        /**
         * Profile name of the app
         */
        profileName?: string;
        /**
         * DRS App name of the app
         */
        DRSAppName?: string;
        /**
         * OPS short name of the app
         */
        shortName?: string;
        /**
         * Local ID of the app
         */
        localId?: number;
    };
    /**
     * Triggered when a SP-enabled game or application starts up
     */
    appStopped?: {
        /**
         * Process ID of the app
         */
        pid?: number;
    };
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInitDataReturn".
 */
export interface GetInitDataReturn {
    micMode?: MicModeEnum;
    /**
     * coplay enable stat
     */
    coplayEnabled?: boolean;
    /**
     * capture state
     */
    captureState?: string;
    desktopCaptureSupported?: boolean;
    desktopCaptureUnsupportedReason?: string[];
    '2KBroadcastSupported'?: boolean;
    captureSupported?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMainViewParameters".
 */
export interface GetMainViewParameters {
    /**
     * If true, will only return the first 3 items, webcamPreset, webcamShown, micPresentCount. False will return all fields
     */
    fetchPartial: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMainViewReturn".
 */
export interface GetMainViewReturn {
    /**
     * webcam present
     */
    webcamPresent?: boolean;
    /**
     * coplay enable stat
     */
    webcamShown?: boolean;
    /**
     * capture state
     */
    captureState?: string;
    desktopCaptureSupported?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "OpenOscParameters".
 */
export interface OpenOscParameters {
    /**
     * Toggles Osc
     */
    open: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "LaunchParameters".
 */
export interface LaunchParameters {
    /**
     * Enable/Disable ShadowPlay
     */
    launch: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetOverlayReadyParameters".
 */
export interface SetOverlayReadyParameters {
    /**
     * True - Overlay Ready, false - Overlay not ready
     */
    bReady: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetShadowPlaySupportedParameters".
 */
export interface GetShadowPlaySupportedParameters {
    /**
     * Gets ShadowPlay Supported
     */
    language: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetShadowPlaySupportedReturn".
 */
export interface GetShadowPlaySupportedReturn {
    MediaPack?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetShadowPlayStatusReturn".
 */
export interface GetShadowPlayStatusReturn {
    launch?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Get4KSupportReturn".
 */
export interface Get4KSupportReturn {
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedBitratesParameters".
 */
export interface GetSupportedBitratesParameters {
    quality: Quality;
    resolution: Resolution;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedBitratesReturn".
 */
export interface GetSupportedBitratesReturn {
    bitrateBpsMin?: number;
    bitrateBpsMax?: number;
    bitrateBpsDefault?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedBitratesFrameratesParameters".
 */
export interface GetSupportedBitratesFrameratesParameters {
    quality: Quality;
    resolution: Resolution;
    codec: Codec;
    framerate?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedBitratesFrameratesReturn".
 */
export interface GetSupportedBitratesFrameratesReturn {
    bitrateBpsMin?: number;
    bitrateBpsMax?: number;
    bitrateBpsDefault?: number;
    fpsDefault?: number;
    supportedFPSCount?: number;
    fpsList?: number[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedResolutionsReturn".
 */
export interface GetSupportedResolutionsReturn {
    resolutions?: Resolution[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedResolutionsCodecsReturn".
 */
export interface GetSupportedResolutionsCodecsReturn {
    resolutions?: Resolution[];
    codecs?: Codec[];
    /**
     * Indicates default video codec
     */
    defaultCodec?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSupportedFrameratesReturn".
 */
export interface GetSupportedFrameratesReturn {
    framerates?: number[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetQualityDefaultDataParameters".
 */
export interface GetQualityDefaultDataParameters {
    quality: Quality;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetQualityDefaultDataReturn".
 */
export interface GetQualityDefaultDataReturn {
    resolution?: Resolution;
    /**
     * Default framerate
     */
    framerate?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCaptureStateReturn".
 */
export interface GetCaptureStateReturn {
    /**
     * ShadowPlay Capture state
     */
    state?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCaptureProcessInfoParameters".
 */
export interface GetCaptureProcessInfoParameters {
    appType: AppType;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCaptureProcessInfoReturn".
 */
export interface GetCaptureProcessInfoReturn {
    /**
     * Game profile name
     */
    profileName?: string;
    /**
     * Game short name
     */
    shortName?: string;
    /**
     * Game DRS name
     */
    DRSAppName?: string;
    /**
     * Process ID of the game
     */
    pid?: number;
    /**
     * CMS ID of the game
     */
    cmsId?: number;
    /**
     * Chroma App ID of the game
     */
    ChromaAppId?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCaptureControlPIDModeReturn".
 */
export interface GetCaptureControlPIDModeReturn {
    /**
     * Indicates valid PID mode
     */
    valid?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetScreenshotSupportReturn".
 */
export interface GetScreenshotSupportReturn {
    /**
     * Indicates screenshot support
     */
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CaptureCustomScreenshotParameters".
 */
export interface CaptureCustomScreenshotParameters {
    /**
     * who is triggering the screenshot
     */
    controller: string;
    /**
     * Name of screenshot file
     */
    screenshotName?: string;
    /**
     * Freestyle Slot Id
     */
    slotId?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CustomScreenshotNotificationsReturn".
 */
export interface CustomScreenshotNotificationsReturn {
    /**
     * Saved screenshot path
     */
    filePath?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetFreestylePreviewBasePathReturn".
 */
export interface GetFreestylePreviewBasePathReturn {
    /**
     * Base folder of freestyle preview
     */
    previewBasePath: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "StartDiskCaptureParameters".
 */
export interface StartDiskCaptureParameters {
    sessionId: SessionIds;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "StopAndSaveDiskCaptureParameters".
 */
export interface StopAndSaveDiskCaptureParameters {
    sessionId: SessionIds;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "StartBufferedCaptureParameters".
 */
export interface StartBufferedCaptureParameters {
    sessionId: SessionIds;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "StopBufferedCaptureParameters".
 */
export interface StopBufferedCaptureParameters {
    sessionId: SessionIds;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SaveBufferedCaptureClipParameters".
 */
export interface SaveBufferedCaptureClipParameters {
    sessionId: SessionIds;
    deltaStartMs: number;
    deltaStopMs: number;
    clipName: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EDGEEventParameters".
 */
export interface EDGEEventParameters {
    /**
     * EDGE event
     */
    event?: {
        [k: string]: any;
    };
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInstantReplayEnableStatusReturn".
 */
export interface GetInstantReplayEnableStatusReturn {
    /**
     * Indicates enable/disable state of Instant Replay
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInstantReplayRunningStatusReturn".
 */
export interface GetInstantReplayRunningStatusReturn {
    /**
     * Indicates running state of Instant Replay
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInstantReplaySettingsReturn".
 */
export interface GetInstantReplaySettingsReturn {
    /**
     * Indicates video record quality
     */
    quality?: string;
    /**
     * Indicates video record resolution
     */
    resolution?: string;
    /**
     * Indicates video record framerate
     */
    framerate?: number;
    /**
     * Indicates video record bitrate
     */
    bitrateBps?: number;
    /**
     * Indicates Instant Replay video length
     */
    replayLengthSeconds?: number;
    /**
     * Indicates video record codec
     */
    codec?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetInstantReplaySettingsParameters".
 */
export interface SetInstantReplaySettingsParameters {
    quality?: Quality;
    replayLengthSeconds?: number;
    resolution?: Resolution;
    framerate?: number;
    bitrateBps?: number;
    codec?: Codec;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableInstantReplayParameters".
 */
export interface EnableInstantReplayParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetLastInstantReplayLengthReturn".
 */
export interface GetLastInstantReplayLengthReturn {
    /**
     * Instant replay length
     */
    lengthSeconds?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInstantReplayCustomizeDataParameters".
 */
export interface GetInstantReplayCustomizeDataParameters {
    quality: Quality;
    resolution: Resolution;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetInstantReplayCustomizeDataReturn".
 */
export interface GetInstantReplayCustomizeDataReturn {
    resolutions?: string[];
    framerates?: number[];
    /**
     * Instant replay buffer length
     */
    replayLengthSeconds?: number;
    /**
     * Indicates video record quality
     */
    quality?: string;
    /**
     * Indicates video record resolution
     */
    resolution?: string;
    /**
     * Indicates video record framereate
     */
    framerate?: number;
    /**
     * Indicates current bitrate
     */
    current?: number;
    /**
     * Indicates min bitrate
     */
    min?: number;
    /**
     * Indicates max bitrate
     */
    max?: number;
    /**
     * Indicates defaulit bitrate
     */
    default?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordEnableStatusReturn".
 */
export interface GetRecordEnableStatusReturn {
    /**
     * Indicates enable/disable state of Manual Record
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordRunningStatusReturn".
 */
export interface GetRecordRunningStatusReturn {
    /**
     * Indicates running state of Manual Record
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableRecordParameters".
 */
export interface EnableRecordParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordSettingsReturn".
 */
export interface GetRecordSettingsReturn {
    /**
     * Indicates video record quality
     */
    quality?: string;
    /**
     * Indicates video record resolution
     */
    resolution?: string;
    /**
     * Indicates video record framereate
     */
    framerate?: number;
    /**
     * Indicates video record bitrate
     */
    bitrateBps?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetRecordSettingsParameters".
 */
export interface SetRecordSettingsParameters {
    quality: Quality;
    resolution?: Resolution;
    framerate?: number;
    bitrateBps?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordCustomizeDataParameters".
 */
export interface GetRecordCustomizeDataParameters {
    quality: Quality;
    resolution: Resolution;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordCustomizeDataReturn".
 */
export interface GetRecordCustomizeDataReturn {
    resolutions?: string[];
    framerates?: string[];
    /**
     * Indicates video record quality
     */
    quality?: string;
    /**
     * Indicates video record resolution
     */
    resolution?: string;
    /**
     * Indicates video record framerate
     */
    framerate?: number;
    /**
     * Indicates current bitrate
     */
    current?: number;
    /**
     * Indicates min bitrate
     */
    min?: number;
    /**
     * Indicates max bitrate
     */
    max?: number;
    /**
     * Indicates defaulit bitrate
     */
    default?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableBroadcastParameters".
 */
export interface EnableBroadcastParameters {
    /**
     * Enables/disables Broadcast
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "PauseBroadcastParameters".
 */
export interface PauseBroadcastParameters {
    /**
     * Pauses/Resumes Broadcast
     */
    pause: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastEnableStatusReturn".
 */
export interface GetBroadcastEnableStatusReturn {
    /**
     * Indicates enable/disable state of Broadcast
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastRunningStatusReturn".
 */
export interface GetBroadcastRunningStatusReturn {
    /**
     * Indicates running state of Broadcast
     */
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastSupportedReturn".
 */
export interface GetBroadcastSupportedReturn {
    /**
     * Indicates support state of Broadcast
     */
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastSettingsReturn".
 */
export interface GetBroadcastSettingsReturn {
    provider?: Provider;
    quality?: Quality;
    resolution?: Resolution;
    framerate?: Framerate;
    /**
     * Indicates broadcast bitrate
     */
    bitrateBps?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastSettingsParameters".
 */
export interface SetBroadcastSettingsParameters {
    provider: Provider;
    quality: Quality;
    resolution?: Resolution;
    /**
     * Indicates broadcast framerate
     */
    framerate?: 30 | 60;
    /**
     * Indicates broadcast bitrate
     */
    bitrateBps?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastSessionParameters".
 */
export interface SetBroadcastSessionParameters {
    /**
     * Indicates data for configuration (0) or live (1)
     */
    type: number;
    provider: Provider;
    /**
     * Broadcast URL
     */
    url?: string;
    /**
     * Port number of the URL
     */
    port?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastPreferredProviderSettingReturn".
 */
export interface GetBroadcastPreferredProviderSettingReturn {
    provider?: Provider;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastPreferredProviderSettingParameters".
 */
export interface SetBroadcastPreferredProviderSettingParameters {
    provider: Provider;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastLastProviderReturn".
 */
export interface GetBroadcastLastProviderReturn {
    provider?: Provider;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastLastProviderParameters".
 */
export interface SetBroadcastLastProviderParameters {
    provider: Provider;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastTitleReturn".
 */
export interface GetBroadcastTitleReturn {
    /**
     * Title of the broadcast
     */
    title?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcast2KReturn".
 */
export interface GetBroadcast2KReturn {
    /**
     * Indicates 2K broadcasting is supported
     */
    support?: boolean;
    /**
     * Indicates 2K broadcasting is enabled
     */
    enabled?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastFacebookLiveSupportReturn".
 */
export interface GetBroadcastFacebookLiveSupportReturn {
    /**
     * Indicates Facebook LiveK broadcasting is supported
     */
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetBroadcastIngestServerReturn".
 */
export interface GetBroadcastIngestServerReturn {
    /**
     * Name of Ingest Server
     */
    server?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastIngestServerParameters".
 */
export interface SetBroadcastIngestServerParameters {
    /**
     * Name of Ingest Server
     */
    server: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastViewerCountImageParameters".
 */
export interface SetBroadcastViewerCountImageParameters {
    /**
     * Image as bitmap data
     */
    image: string;
    /**
     * Image as bitmap data
     */
    imageWidth: number;
    /**
     * Image as bitmap data
     */
    imageHeight: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetBroadcastViewerMaxParameters".
 */
export interface SetBroadcastViewerMaxParameters {
    /**
     * Number of maximum viewers allowed
     */
    count: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordBroadcastConcurrencySupportReturn".
 */
export interface GetRecordBroadcastConcurrencySupportReturn {
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetRecordGamestreamConcurrencySupportReturn".
 */
export interface GetRecordGamestreamConcurrencySupportReturn {
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetDesktopCaptureEnabledReturn".
 */
export interface GetDesktopCaptureEnabledReturn {
    enable?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetDesktopCaptureEnabledParameters".
 */
export interface SetDesktopCaptureEnabledParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetDesktopCaptureSupportedReturn".
 */
export interface GetDesktopCaptureSupportedReturn {
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetDesktopCaptureSupportReasonReturn".
 */
export interface GetDesktopCaptureSupportReasonReturn {
    support?: boolean;
    desktopCaptureUnsupportedReason?: string[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHevcSupportedStateReturn".
 */
export interface GetHevcSupportedStateReturn {
    enable?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetAudioModeReturn".
 */
export interface GetAudioModeReturn {
    mode?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetAudioModeParameters".
 */
export interface SetAudioModeParameters {
    mode: Mode;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetAudioSettingsReturn".
 */
export interface GetAudioSettingsReturn {
    systemVolumePercent?: number;
    separateTracks?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetAudioSettingsParameters".
 */
export interface SetAudioSettingsParameters {
    systemVolumePercent: number;
    separateTracks: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMicCountReturn".
 */
export interface GetMicCountReturn {
    present?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMicModeReturn".
 */
export interface GetMicModeReturn {
    mode?: MicModeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetMicModeParameters".
 */
export interface SetMicModeParameters {
    mode: MicModeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMicSettingsParameters".
 */
export interface GetMicSettingsParameters {
    index: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetMicSettingsReturn".
 */
export interface GetMicSettingsReturn {
    index?: number;
    name?: string;
    id?: string;
    muted?: boolean;
    enabled?: boolean;
    volumePercent?: number;
    boostPercent?: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetMicSettingsParameters".
 */
export interface SetMicSettingsParameters {
    index: number;
    muted: boolean;
    volumePercent: number;
    boostPercent: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "MicPTTModeParameters".
 */
export interface MicPTTModeParameters {
    mode: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableWebcamParameters".
 */
export interface EnableWebcamParameters {
    status: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetWebcamEnableReturn".
 */
export interface GetWebcamEnableReturn {
    status?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetWebcamShownReturn".
 */
export interface GetWebcamShownReturn {
    shown?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetWebcamPresentReturn".
 */
export interface GetWebcamPresentReturn {
    present?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetWebcamPreviewStateReturn".
 */
export interface GetWebcamPreviewStateReturn {
    enabled?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableWebcamPreviewParameters".
 */
export interface EnableWebcamPreviewParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHighlightsActiveReturn".
 */
export interface GetHighlightsActiveReturn {
    /**
     * Flag indicating if there is an active Highlight session
     */
    active: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHighlightsEnableReturn".
 */
export interface GetHighlightsEnableReturn {
    /**
     * Flag indicating Highlights enable state
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetHighlightsEnableParameters".
 */
export interface SetHighlightsEnableParameters {
    /**
     * Flag indicating Highlights enable state
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSDKInstanceActiveReturn".
 */
export interface GetSDKInstanceActiveReturn {
    /**
     * Flag indicating active SDK instance
     */
    active: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSDKPermissionsParameters".
 */
export interface GetSDKPermissionsParameters {
    app: HighlightAppInfo;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetSDKPermissionsReturn".
 */
export interface GetSDKPermissionsReturn {
    permissions: SdkPermissionsArray;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetSDKPermissionsParameters".
 */
export interface SetSDKPermissionsParameters {
    app: HighlightAppInfo;
    permissions: SdkPermissionsArray;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "NotifyAppOverlayStateParameters".
 */
export interface NotifyAppOverlayStateParameters {
    /**
     * Open or closed state of overlay
     */
    open: boolean;
    state: State;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHighlightsConfigParameters".
 */
export interface GetHighlightsConfigParameters {
    app: HighlightAppInfo;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHighlightsConfigReturn".
 */
export interface GetHighlightsConfigReturn {
    /**
     * Array of highlight definitions
     */
    highlightDefinitions: HighlightDefinition[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetHighlightsConfigParameters".
 */
export interface SetHighlightsConfigParameters {
    app: HighlightAppInfo;
    highlightDefinitions: HighlightDefinition[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetApplicationConfigsReturn".
 */
export interface GetApplicationConfigsReturn {
    /**
     * Array of applications and their Highlights information
     */
    apps: {
        app?: HighlightAppInfo;
        permissions?: SdkPermissionsArray;
        /**
         * Array Highlights information for this application
         */
        highlights?: HighlightDefinition[];
    }[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHotkeyShortcutParameters".
 */
export interface GetHotkeyShortcutParameters {
    hotkey: HotkeyEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHotkeyShortcutReturn".
 */
export interface GetHotkeyShortcutReturn {
    /**
     * Array of integer values for the keys
     */
    keys?: number[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetHotkeyShortcutParameters".
 */
export interface SetHotkeyShortcutParameters {
    hotkey: HotkeyEnum;
    /**
     * Array of integer values for the keys
     */
    keys: number[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetHotkeyMonitoringReturn".
 */
export interface GetHotkeyMonitoringReturn {
    /**
     * Defines if monitoring is enabled
     */
    enable?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "InitParameters".
 */
export interface InitParameters {
    osType: string;
    browserType?: string;
    areHotkeysOverridden: boolean;
    /**
     * Array of integer values for the keys
     */
    overriddenHotkeysList: number[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "InitReturn".
 */
export interface InitReturn {
    success: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetHotkeyMonitoringParameters".
 */
export interface SetHotkeyMonitoringParameters {
    /**
     * Defines if monitoring is enabled
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetHotkeyDynamicEnableParameters".
 */
export interface SetHotkeyDynamicEnableParameters {
    /**
     * Defines if monitoring is enabled
     */
    enable: boolean;
    /**
     * Array of hotkeys to toggle for dynamic enable
     */
    keys: HotkeyEnum[];
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetIndicatorSupportedParameters".
 */
export interface GetIndicatorSupportedParameters {
    id: IndicatorEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetIndicatorSupportedReturn".
 */
export interface GetIndicatorSupportedReturn {
    /**
     * Defines if the indicator is supported
     */
    support?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetIndicatorOverlaySettingsParameters".
 */
export interface GetIndicatorOverlaySettingsParameters {
    id: IndicatorEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetIndicatorOverlaySettingsReturn".
 */
export interface GetIndicatorOverlaySettingsReturn {
    /**
     * Defines if monitoring is enabled
     */
    enable?: boolean;
    position?: OverlayPositionEnum;
    size?: OverlaySizeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetIndicatorOverlaySettingsParameters".
 */
export interface SetIndicatorOverlaySettingsParameters {
    id: IndicatorEnum;
    /**
     * Defines if monitoring is enabled
     */
    enable: boolean;
    position: OverlayPositionEnum;
    size?: OverlaySizeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayDefaultPathReturn".
 */
export interface GetCustomOverlayDefaultPathReturn {
    /**
     * Fully quantified path of a folder
     */
    path?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlaySupportReturn".
 */
export interface GetCustomOverlaySupportReturn {
    supportType?: SupportType;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayPathParameters".
 */
export interface GetCustomOverlayPathParameters {
    id: CustomOverlayTypeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayPathReturn".
 */
export interface GetCustomOverlayPathReturn {
    /**
     * Fully quantified path of a folder
     */
    path?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetCustomOverlayPathParameters".
 */
export interface SetCustomOverlayPathParameters {
    id: CustomOverlayTypeEnum;
    /**
     * Fully quantified path of a folder
     */
    path: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayEnableParameters".
 */
export interface GetCustomOverlayEnableParameters {
    id: CustomOverlayTypeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayEnableReturn".
 */
export interface GetCustomOverlayEnableReturn {
    /**
     * Enable state of the Custom Overlay
     */
    enable?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetCustomOverlayEnableParameters".
 */
export interface SetCustomOverlayEnableParameters {
    id: CustomOverlayTypeEnum;
    /**
     * Enable state of the Custom Overlay
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayDisplayParameters".
 */
export interface GetCustomOverlayDisplayParameters {
    id: CustomOverlayTypeEnum;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetCustomOverlayDisplayReturn".
 */
export interface GetCustomOverlayDisplayReturn {
    /**
     * Display state of the Custom Overlay
     */
    display?: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SelectCustomOverlayParameters".
 */
export interface SelectCustomOverlayParameters {
    /**
     * ID of Custom Overlay
     */
    id: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "EnableCustomOverlayParameters".
 */
export interface EnableCustomOverlayParameters {
    /**
     * State of the Custom Overlay feature
     */
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetNvCameraToggleStateReturn".
 */
export interface GetNvCameraToggleStateReturn {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SetNvCameraToggleStateParameters".
 */
export interface SetNvCameraToggleStateParameters {
    enable: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "ToggleAutoHighlightParameters".
 */
export interface ToggleAutoHighlightParameters {
    /**
     * Flag indicating enable/disable AHL
     */
    toggle: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "DisableRecordSpecialCaseParameters".
 */
export interface DisableRecordSpecialCaseParameters {
    /**
     * Enum indicating to disable recording of SDK Highlights
     */
    specialCase: number;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GetExportedGFESettingsDataReturn".
 */
export interface GetExportedGFESettingsDataReturn {
    isValidGFEDataFound: boolean;
    gfeData?: string;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SyncShadowPlaySettingsParameters".
 */
export interface SyncShadowPlaySettingsParameters {
    isGFEMigration: boolean;
}
/**
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GAssistToggleParameters".
 */
export interface GAssistToggleParameters {
    systemAssistEnable: GAssistFeatureState;
    gameAssistEnable: GAssistFeatureState;
    voiceQueryEnable: GAssistFeatureState;
}

/**
 * Permission values for SDK (unknown assumes implicit mustAsk)
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SDKPermissions".
 */
export enum SdkPermissions {
    granted = 'granted',
    mustAsk = 'mustAsk',
    denied = 'denied'
}
/**
 * Codec
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Codec".
 */
export enum Codec {
    H264_HEVC = 'H264/HEVC',
    AV1 = 'AV1',
    H265 = 'H265'
}
/**
 * Enum of custom overlay types
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CustomOverlayTypeEnum".
 */
export enum CustomOverlayTypeEnum {
    custom = 'custom',
    customA = 'customA',
    customB = 'customB',
    customC = 'customC'
}
/**
 * Enum of possible indicator overlay sizes
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "OverlaySizeEnum".
 */
export enum OverlaySizeEnum {
    small = 'small',
    medium = 'medium',
    large = 'large'
}
/**
 * Enum of possible indicator overlay positions. Not all positions apply to each indicator.
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "OverlayPositionEnum".
 */
export enum OverlayPositionEnum {
    leftTop = 'leftTop',
    leftCenter = 'leftCenter',
    leftBottom = 'leftBottom',
    rightTop = 'rightTop',
    rightCenter = 'rightCenter',
    rightBottom = 'rightBottom',
    centerTop = 'centerTop',
    centerCenter = 'centerCenter',
    centerBottom = 'centerBottom',
    floatingRandom = 'floatingRandom',
    floatingBorder = 'floatingBorder',
    none = 'none'
}
/**
 * Enum of possible Share-specific indicator overlay types. The "comments" value defined only for completeness and not wired up.
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "IndicatorEnum".
 */
export enum IndicatorEnum {
    record = 'record',
    gamecast = 'gamecast',
    fps = 'fps',
    viewer = 'viewer',
    rig = 'rig',
    webcam = 'webcam',
    comments = 'comments'
}
/**
 * Resolution
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Resolution".
 */
export enum Resolution {
    In_game = 'In-game',
    Resolution_4320p_8K = '4320p 8K',
    Resolution_2160p_4K = '2160p 4K',
    Resolution_1440p_HD = '1440p HD',
    Resolution_1080p_HD = '1080p HD',
    Resolution_720p_HD = '720p HD',
    Resolution_480p = '480p',
    Resolution_360p = '360p',
    Resolution_240p = '240p',
    Exact = 'Exact'
}
/**
 * Enum of possible MIC mode
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "MicModeEnum".
 */
export enum MicModeEnum {
    on = 'on',
    off = 'off',
    alwayson = 'alwayson',
    ptt = 'ptt'
}
/**
 * Enum of possible capture type
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CaptureModeEnum".
 */
export enum CaptureModeEnum {
    manual = 'manual',
    dvr = 'dvr',
    gameCast = 'gameCast',
    highlight = 'highlight',
    gearRecord = 'gearRecord',
    gearHighlight = 'gearHighlight',
    lastMode = 'lastMode',
    screenshot = 'screenshot'
}
/**
 * Type of SDK object/scope to apply permissions
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SDKScopeType".
 */
export enum SdkScopeType {
    highlights = 'highlights',
    highlightsRecordVideo = 'highlightsRecordVideo',
    highlightsRecordScreenshot = 'highlightsRecordScreenshot'
}
/**
 * Enum of possible device state
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "DeviceStateTypeEnum".
 */
export enum DeviceStateTypeEnum {
    plugged_in = 'plugged_in',
    plugged_out = 'plugged_out',
    enabled = 'enabled',
    disabled = 'disabled'
}
/**
 * Enum of possible device type
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "DeviceTypeEnum".
 */
export enum DeviceTypeEnum {
    mic = 'mic',
    webcam = 'webcam'
}
/**
 * Enum of possible Cloud Session State type
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "CloudSessionStateType".
 */
export enum CloudSessionStateType {
    unknown = 'unknown',
    started = 'started',
    stopped = 'stopped',
    paused = 'paused',
    resumed = 'resumed'
}
/**
 * Enum of possible Share-specific hotkey actions
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "hotkeyEnum".
 */
export enum HotkeyEnum {
    OpenIGO = 'OpenIGO',
    OpenFreestyle = 'OpenFreestyle',
    OpenAnsel = 'OpenAnsel',
    PTT = 'PTT',
    FPS = 'FPS',
    Screenshot = 'Screenshot',
    DVRSave = 'DVRSave',
    RecordToggle = 'RecordToggle',
    BroadcastToggle = 'BroadcastToggle',
    BroadcastPauseToggle = 'BroadcastPauseToggle',
    CameraToggle = 'CameraToggle',
    OverlayToggle = 'OverlayToggle',
    CustomOverlayA = 'CustomOverlayA',
    CustomOverlayB = 'CustomOverlayB',
    CustomOverlayC = 'CustomOverlayC',
    CommentsToggle = 'CommentsToggle',
    MicToggle = 'MicToggle',
    DVRToggle = 'DVRToggle',
    FreestyleToggle = 'FreestyleToggle',
    FreestylePresentCycle = 'FreestylePresentCycle',
    FreeStyleToggleStyle1 = 'FreeStyleToggleStyle1',
    FreeStyleToggleStyle2 = 'FreeStyleToggleStyle2',
    FreeStyleToggleStyle3 = 'FreeStyleToggleStyle3',
    NativeKeyboardInputDialog = 'NativeKeyboardInputDialog',
    ConnectGfnBroadcastToggle = 'ConnectGfnBroadcastToggle',
    StatisticsToggle = 'StatisticsToggle',
    QuitGame = 'QuitGame',
    PMOCOverlay = 'PMOCOverlay',
    PMOCFontSizeCycle = 'PMOCFontSizeCycle',
    PMOCOverlayCycle = 'PMOCOverlayCycle',
    PMOCResetAverageMetrics = 'PMOCResetAverageMetrics',
    PMOCLoggingToggle = 'PMOCLoggingToggle',
    InStreamPaste = 'InStreamPaste',
    ChatUIToggle = 'ChatUIToggle',
    ChatPTT = 'ChatPTT'
}
/**
 * Enum of possible GAssist feature states
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "GAssistFeatureState".
 */
export enum GAssistFeatureState {
    SPFeatureState_Unknown = 0,
    SPFeatureState_Start = 1,
    SPFeatureState_Stop = 2
}
/**
 * Type of the highlight
 */
export enum Type {
    screenshot = 'screenshot',
    video = 'video'
}
/**
 * Quality
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Quality".
 */
export enum Quality {
    UltraGood = 'UltraGood',
    VeryGood = 'VeryGood',
    Good = 'Good',
    Average = 'Average',
    Custom = 'Custom',
    GamecastYTLUltraGood = 'GamecastYTLUltraGood',
    GamecastYTLVeryGood = 'GamecastYTLVeryGood',
    GamecastYTLGood = 'GamecastYTLGood',
    GamecastYTLAverage = 'GamecastYTLAverage',
    GamecastUltraGood = 'GamecastUltraGood',
    GamecastVeryGood = 'GamecastVeryGood',
    GamecastGood = 'GamecastGood',
    GamecastAverage = 'GamecastAverage',
    GamecastFBLVeryGood = 'GamecastFBLVeryGood',
    GamecastFBLGood = 'GamecastFBLGood',
    GamecastFBLAverage = 'GamecastFBLAverage'
}
/**
 * Possible session types to start a recording with
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "SessionIds".
 */
export enum SessionIds {
    instantReplay = 'instantReplay',
    manualRecord = 'manualRecord',
    highlights = 'highlights',
    gear = 'gear'
}
/**
 * Indicates broadcast provider/destination
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "Provider".
 */
export enum Provider {
    Always_Ask = 'Always Ask',
    Twitch = 'Twitch',
    YouTube_Live = 'YouTube Live',
    Facebook_Live = 'Facebook Live',
    Do_Not_Broadcast = 'Do Not Broadcast'
}
/**
 * Enum defining status
 *
 * This interface was referenced by `ShareServer`'s JSON-Schema
 * via the `definition` "InternalErrorCodeEnum".
 */
export enum InternalErrorCodeEnum {
    GXP_Success_Ok = 0,
    GXP_Success_NoError = 1,
    GXP_Error_NotImplemented = -2147467263,
    GXP_Error_NoInterface = -2147467262,
    GXP_Error_InvalidPointer = -2147467261,
    GXP_Error_Abort = -2147467260,
    GXP_Error_Fail = -2147467259,
    GXP_Error_Unexpected = -2147468113,
    GXP_Error_FileNotFound = -2147024894,
    GXP_Error_PathNotFound = -2147024893,
    GXP_Error_InvalidHandle = -2147024890,
    GXP_Error_InvalidData = -2147024883,
    GXP_Error_OutOfMemory = -2147024882,
    GXP_Error_InvalidArg = -2147024809,
    GXP_Error_TimeOut = -2147023436,
    GXP_Error_FileTooLarge = -2147024673,
    GXP_Error_MaxStartAttempts = -2146959359,
    ShadowPlay_OSC_ERR_GENERIC = -1,
    ShadowPlay_OSC_ERR_INVALID_VER = -2,
    ShadowPlay_OSC_ERR_CLIENT_UNINITIALIZED = -3,
    ShadowPlay_OSC_ERR_SERVER_NOT_CONNECTED = -4,
    ShadowPlay_OSC_ERR_SERVER_TIME_OUT = -5,
    ShadowPlay_OSC_ERR_SERVER_CANNOT_CONNECT = -6,
    ShadowPlay_OSC_ERR_STREAM_NOT_FOUND = -7,
    ShadowPlay_OSC_ERR_INVALID_FORMAT = -8,
    ShadowPlay_OSC_ERR_INVALID_PARAMETERS = -9,
    ShadowPlay_OSC_ERR_DX = -10,
    ShadowPlay_OSC_ERR_MMF = -11,
    ShadowPlay_OSC_ERR_INSUFFICIENT_BUFFER = -12,
    ShadowPlay_OSC_ERR_NO_IMPLEMENTATION = -13,
    ShadowPlay_OSC_ERR_STREAM_LOCKED = -14,
    ShadowPlay_OSC_ERR_ALREADY_REGISTERED = -15,
    ShadowPlay_OSC_ERR_ALREADY_CREATED = -16,
    ShadowPlay_OSC_ERR_PROTOBUF = -17,
    ShadowPlay_OSC_ERR_NOTAVAILABLE = -18,
    ShadowPlay_OSC_ERR_OUT_OF_MEMORY = -19,
    ShadowPlay_OSC_ERR_ABANDONED = -20,
    ShadowPlay_OSC_ERR_INVALID_CALL = -21,
    ShadowPlay_OSC_ERR_NO_VALID_GAME_RUNNING = -22,
    ShadowPlay_E_SERVER_NOT_RUNNING = -2147220959,
    ShadowPlay_E_UNKNOWN_PROP_NAME = -2147220958,
    ShadowPlay_E_INVALID_PROP_FORMAT = -2147220957,
    ShadowPlay_E_INVALID_PROP_VALUE = -2147220956,
    ShadowPlay_E_UNKNOWN_CMD_NAME = -2147220955,
    ShadowPlay_E_INVALID_CMD_FORMAT = -2147220954,
    ShadowPlay_E_INVALID_CMD_VERSION = -2147220953,
    ShadowPlay_E_NOT_READY = -2147220952,
    ShadowPlay_E_SERVER_IN_EXCLUSIVE_MODE = -2147220951,
    ShadowPlay_E_SERVER_IN_GAMECAST_MODE = -2147220950,
    ShadowPlay_E_CANNOT_CLAIM_H264_LICENSE = -2147220949,
    ShadowPlay_E_FAIL_SYSTEM_REQUIREMENTS_OS = -2147220948,
    ShadowPlay_E_FAIL_SYSTEM_REQUIREMENTS_RAM = -2147220947,
    ShadowPlay_E_FAIL_SYSTEM_REQUIREMENTS_GPU = -2147220946,
    ShadowPlay_E_FAIL_ENABLE_SHADOWPLAY = -2147220945,
    ShadowPlayE_FAIL_ENABLE_NVFBC = -2147220944,
    ShadowPlay_E_FAIL_SYSTEM_REQUIREMENTS_DRIVER = -2147220943,
    ShadowPlay_E_FAIL_ENABLE_NVFBC_TIMEOUT = -2147220942,
    ShadowPlay_E_FAIL_ENABLE_OSC = -2147220941,
    ShadowPlay_E_FAIL_ENABLE_HELPER = -2147220940,
    ShadowPlay_E_FAIL_NO_GALLERY_SPACE = -2147220926,
    ShadowPlay_E_FAIL_NO_DISC_SPACE = -2147220938,
    ShadowPlay_E_FAIL_CAPTURE_DISABLED = -2147220937,
    ShadowPlay_E_BOUNDS = -2147483637,
    ShadowPlay_E_NOT_VALID_STATE = -2147019873,
    ShadowPlay_E_UNEXPECTED = -2147418113,
    GXP_Error_HotkeyInUse = -2146897916
}
/**
 * Capture state
 */
export enum RecordingState {
    available = 'available',
    started = 'started',
    auto_pause = 'auto_pause',
    user_pause = 'user_pause',
    user_pause_auto_pause = 'user_pause_auto_pause',
    last = 'last',
    error = 'error',
    saved = 'saved',
    restarted_notifier = 'restarted_notifier',
    resumed_notifier = 'resumed_notifier',
    notifier_none = 'notifier_none',
    invalid = 'invalid',
    enabled = 'enabled',
    disabled = 'disabled'
}
export enum AppType {
    capture = 'capture',
    in_focus = 'in-focus',
    last_proc = 'last-proc'
}
/**
 * Indicates broadcast framerate
 */
export enum Framerate {
    _30 = 30,
    _60 = 60
}
export enum Mode {
    off = 'off',
    game = 'game',
    mic = 'mic',
    both = 'both'
}
/**
 * The type of overlay UI in the current state
 */
export enum State {
    main = 'main',
    permission = 'permission',
    highlightsSummary = 'highlightsSummary'
}
/**
 * Level of custom overlay support
 */
export enum SupportType {
    none = 'none',
    single = 'single',
    multiple = 'multiple'
}


}
